import { find, groupBy } from 'lodash-es';
import { normalize } from 'normalizr';

import {
  type AssetsAssetTranscriptionPropertiesSchemaWithIdNullable,
  httpGetAssetsByAssetIdSegmentsVtt,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  type FilesFileCreateSchema,
  type FilesFileSchemaPartial,
  type FilesFileSchemaWithIdNullable,
  type FilesFileSetSchema,
  type FilesFileSetSchemaWithIdNullable,
  type FilesFormatSchema,
  type FilesFormatSchemaWithId,
  type FilesHttpGetAssetsByAssetIdVersionsByVersionIdFileSetsQuery,
  type FilesHttpGetAssetsByAssetIdVersionsByVersionIdProxiesQuery,
  type FilesProxySchemaWithId,
  httpDeleteAssetsByAssetIdFileSetsByFileSetId,
  httpDeleteAssetsByAssetIdFormatsByFormatId,
  httpDeleteAssetsByAssetIdProxiesByProxyId,
  httpDeleteAssetsByAssetIdSubtitlesBySubtitleId,
  httpGetAssetsByAssetIdFileSets,
  httpGetAssetsByAssetIdFileSetsByFileSetId,
  httpGetAssetsByAssetIdFileSetsByFileSetIdFiles,
  httpGetAssetsByAssetIdFiles,
  httpGetAssetsByAssetIdFilesByFileIdDownloadUrl,
  httpGetAssetsByAssetIdFilesByFileIdIsgHandlerUrl,
  httpGetAssetsByAssetIdFormats,
  httpGetAssetsByAssetIdProxies,
  httpGetAssetsByAssetIdProxiesByProxyIdDownloadUrl,
  httpGetAssetsByAssetIdSubtitles,
  httpGetAssetsByAssetIdVersionsByVersionIdFileSets,
  httpGetAssetsByAssetIdVersionsByVersionIdFiles,
  httpGetAssetsByAssetIdVersionsByVersionIdFormats,
  httpGetAssetsByAssetIdVersionsByVersionIdProxies,
  httpGetAssetsByAssetIdVersionsByVersionIdSubtitles,
  httpPatchAssetsByAssetIdFilesByFileId,
  httpPatchAssetsByAssetIdFormatsByFormatId,
  httpPostAssetsByAssetIdFileSets,
  httpPostAssetsByAssetIdFiles,
  httpPostAssetsByAssetIdFormats,
} from '@iconik-io/iconik-api/src/services/files';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import {
  itemsToDownloadRequest,
  typeFormatMap,
} from '@iconik-io/shared-lib/src/agent/items_to_download_request';
import { uniqueFormats } from '@iconik-io/shared-lib/src/analytics/download_event';
import {
  fetchAllPagesByLastId,
  fetchAllPagesVersionedByLastId,
  maxObjectsPerFetch,
} from '@iconik-io/shared-lib/src/redux/asset/thunks';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { entitySchemas } from '@iconik-io/shared-lib/src/redux/entities/schemas';
import { selectFilesByFileSetId } from '@iconik-io/shared-lib/src/redux/entities/selectors/files';
import { selectLatestActiveVersionExtended } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import {
  deleteNormalizedEntity,
  updateEntities,
} from '@iconik-io/shared-lib/src/redux/entities/slice';
import type { PartialEntities } from '@iconik-io/shared-lib/src/redux/entities/types';
import {
  findOriginalCloudFormat,
  getAllTranscriptions,
  getAssetSubtitlePromises,
  getTranscriptionProperties,
} from '@iconik-io/shared-lib/src/redux/files/thunks';

import { agentApiClient } from '../../agent_api_client';
import DownloadAgentErrorMessage from '../../components/agent/download_agent_error_message';
import {
  SHARE_ASSET_PAGE_ROUTE,
  SHARE_ASSET_VERSION_PAGE_ROUTE,
} from '../../constants/routes';
import { getCurrentRouteParams } from '../../router/current_route_params';
import type { AppDispatch, GetState } from '../../store/types';
import downloadUrlIframe from '../../utils/download_url_iframe';
import { selectUserRoles } from '../../utils/get_user_roles';
import { agentTryDownload } from '../agent/thunks';
import { selectTranscriptionPropertiesByAssetId } from '../asset_transcriptions/selectors';
import {
  errorMessage,
  successMessage,
  warningMessage,
} from '../messages/thunks';
import { selectCurrentVersionId } from '../version/selectors';
import { loadingAssetFormatsData, setAssetSubtitles } from './slice';
import { trackDownloadEvent } from './tracking';
import type { FilesAssetSubtitle } from './types';

/**
 * Who took the download, so a tracked wrapper counts neither the Agent's work
 * nor a request that never left the browser.
 */
type DownloadHandledBy = 'agent' | 'browser' | 'none';

function getDownloadFile(assetId: string, fileId: string) {
  return (dispatch: AppDispatch): Promise<string | undefined> =>
    dispatch(
      httpGetAssetsByAssetIdFilesByFileIdDownloadUrl(assetId, fileId),
    ).then((response) => {
      if (!response.body.url) {
        dispatch(errorMessage({ text: 'Download url is empty' }));
        return undefined;
      }
      return response.body.url;
    });
}

const trackBrowserAssetDownload =
  (formats: readonly string[] | undefined, itemCount = 1) =>
  (dispatch: AppDispatch) =>
    dispatch(
      trackDownloadEvent({
        formats,
        itemCount,
        objectTypes: [OBJECT_TYPE_ASSETS],
        handledBy: 'Browser',
      }),
    );

export function downloadFileById(assetId: string, fileId: string) {
  return (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    dispatch(getDownloadFile(assetId, fileId)).then((url) => {
      if (url) {
        downloadUrlIframe(url);
        const state = getState();
        const formatId = state.entities.files[fileId]?.format_id;
        const formatName = formatId
          ? state.entities.formats[formatId]?.name
          : undefined;
        dispatch(
          trackBrowserAssetDownload(formatName ? [formatName] : undefined),
        );
      } else {
        dispatch(errorMessage({ text: 'File not found' }));
      }
    });
}

const downloadAttempts =
  (
    assetId: string,
    cloudFileSetFiles: FilesFileSchemaWithIdNullable[][],
    index: number,
    error: string,
  ) =>
  // if a file_set download fails will attempt to download the next file_set
  // Only useful when having a format stored in different cloud storages
  async (dispatch: AppDispatch): Promise<void> => {
    const fileSetFiles = cloudFileSetFiles[index];
    if (!fileSetFiles) {
      return;
    }
    try {
      const urls = await Promise.all(
        fileSetFiles.map((file) => dispatch(getDownloadFile(assetId, file.id))),
      );
      urls.forEach((url) => {
        if (url) {
          downloadUrlIframe(url);
        }
      });
    } catch {
      if (index + 1 < cloudFileSetFiles.length) {
        await dispatch(
          downloadAttempts(assetId, cloudFileSetFiles, index + 1, error),
        );
      }
    }
  };

export function getAssetProxyRequest(
  assetId: string,
  versionId: string | undefined,
  data: FilesHttpGetAssetsByAssetIdVersionsByVersionIdProxiesQuery = {},
) {
  return versionId
    ? httpGetAssetsByAssetIdVersionsByVersionIdProxies(assetId, versionId, data)
    : httpGetAssetsByAssetIdProxies(assetId, data);
}

export function getAssetProxy(assetId: string, versionId?: string) {
  return async (dispatch: AppDispatch) =>
    dispatch(getAssetProxyRequest(assetId, versionId));
}

export function getAssetFiles(assetId: string, versionId?: string) {
  return (dispatch: AppDispatch) => {
    const query = { generate_signed_url: false };
    return versionId
      ? fetchAllPagesVersionedByLastId(
          assetId,
          versionId,
          query,
          dispatch,
          httpGetAssetsByAssetIdVersionsByVersionIdFiles,
        )
      : fetchAllPagesByLastId(
          assetId,
          query,
          dispatch,
          httpGetAssetsByAssetIdFiles,
        );
  };
}

export const getAssetFileSets =
  (
    assetId: string,
    query: FilesHttpGetAssetsByAssetIdVersionsByVersionIdFileSetsQuery = {},
    versionId?: string,
  ) =>
  (dispatch: AppDispatch) => {
    const warnAboutLimit = () => {
      dispatch(
        warningMessage({
          text: `This asset has more than ${maxObjectsPerFetch} file sets. Only the first ${maxObjectsPerFetch} are shown.`,
        }),
      );
    };

    return versionId
      ? fetchAllPagesVersionedByLastId(
          assetId,
          versionId,
          query,
          dispatch,
          httpGetAssetsByAssetIdVersionsByVersionIdFileSets,
          warnAboutLimit,
        )
      : fetchAllPagesByLastId(
          assetId,
          query,
          dispatch,
          httpGetAssetsByAssetIdFileSets,
          warnAboutLimit,
        );
  };

export function getAssetSubtitles(assetId: string, versionId?: string) {
  return async (dispatch: AppDispatch, getState: GetState): Promise<void> => {
    const currentVersionId =
      versionId ??
      selectCurrentVersionId(
        getState(),
        assetId,
        getCurrentRouteParams().versionId,
      );
    // Hide video analysis subtitles from the share page
    const { route } = getState().routeParams;
    const isShare = [
      SHARE_ASSET_PAGE_ROUTE,
      SHARE_ASSET_VERSION_PAGE_ROUTE,
    ].includes(route);

    const allowViewTranscriptions =
      getState().share.share.allow_view_transcriptions;

    let transcriptionProperties:
      | AssetsAssetTranscriptionPropertiesSchemaWithIdNullable[]
      | undefined;

    const loadTranscriptions = !isShare || (isShare && allowViewTranscriptions);

    if (currentVersionId && loadTranscriptions) {
      await dispatch(getTranscriptionProperties(assetId, currentVersionId));
      transcriptionProperties = selectTranscriptionPropertiesByAssetId(
        getState(),
        assetId,
        versionId,
      );
    }

    const params = { per_page: 100 };
    const subtitlesResponse = currentVersionId
      ? await dispatch(
          httpGetAssetsByAssetIdVersionsByVersionIdSubtitles(
            assetId,
            currentVersionId,
            params,
          ),
        )
      : await dispatch(httpGetAssetsByAssetIdSubtitles(assetId, params));

    const { objects: assetSubtitles = [] } = subtitlesResponse.body;

    const vttResponses: FilesAssetSubtitle[] = [];
    const promises: Promise<FilesAssetSubtitle>[] = getAssetSubtitlePromises(
      dispatch,
      assetSubtitles,
      assetId,
      currentVersionId,
      vttResponses,
    );

    if (loadTranscriptions && transcriptionProperties) {
      const allTranscriptions = getAllTranscriptions(
        dispatch,
        transcriptionProperties,
        assetId,
        vttResponses,
        currentVersionId,
      );
      promises.push(...allTranscriptions);
    }

    if (!isShare) {
      promises.push(
        dispatch(
          httpGetAssetsByAssetIdSegmentsVtt(assetId, {
            segment_type: 'TAG',
            version_id: currentVersionId,
          }),
        ).then((vttResponse) => {
          const blobURL = window.URL.createObjectURL(
            new Blob([vttResponse.body], { type: 'text/vtt' }),
          );
          // A client-side entry for the analysis VTT, which has no subtitle
          // record behind it and so carries none of the fields describing one.
          // biome-ignore lint/plugin: see above.
          const response = {
            asset_id: assetId,
            id: `${assetId}_tags`,
            name: 'Video Analysis',
            url: blobURL,
          } as FilesAssetSubtitle;
          vttResponses.push(response);
          return response;
        }),
      );
    }

    Promise.allSettled(promises).finally(() => {
      dispatch(
        setAssetSubtitles(vttResponses.filter((subtitle) => subtitle.id)),
      );
    });
  };
}

export function getAssetFormats(assetId: string, versionId?: string) {
  return (dispatch: AppDispatch) =>
    versionId
      ? fetchAllPagesVersionedByLastId(
          assetId,
          versionId,
          {},
          dispatch,
          httpGetAssetsByAssetIdVersionsByVersionIdFormats,
        )
      : fetchAllPagesByLastId(
          assetId,
          {},
          dispatch,
          httpGetAssetsByAssetIdFormats,
        );
}

export const deleteAssetFormatRequest = (
  assetId: string,
  formatId: string,
  immediately: boolean,
  isProxy: boolean,
) => {
  if (isProxy) {
    return httpDeleteAssetsByAssetIdProxiesByProxyId(assetId, formatId);
  }
  return httpDeleteAssetsByAssetIdFormatsByFormatId(assetId, formatId, {
    immediately: immediately ? 'true' : 'false',
  });
};

export function deleteAssetFormat(
  assetId: string,
  formatId: string,
  immediately = false,
  isProxy = false,
) {
  return (dispatch: AppDispatch, getState: GetState) =>
    dispatch(
      deleteAssetFormatRequest(assetId, formatId, immediately, isProxy),
    ).then(() => {
      const state = getState();

      // if there is a subtitle associated then delete it
      const subtitles = state.filesReducer.assetSubtitles;
      const subtitle = find(subtitles, { format_id: formatId });
      if (subtitle) {
        dispatch(
          httpDeleteAssetsByAssetIdSubtitlesBySubtitleId(assetId, subtitle.id),
        );
      }

      // delete format reference from the corresponding search item
      const searchItem = state.entities.search_items[assetId];
      if (searchItem?.formats) {
        dispatch(
          updateEntities({
            search_items: {
              [searchItem.id]: {
                ...searchItem,
                formats: searchItem.formats.filter((id) => id !== formatId),
              },
            },
          }),
        );
      }

      dispatch(deleteNormalizedEntity({ key: 'formats', id: formatId }));
    });
}

export function updateFileRequest(
  assetId: string,
  fileId: string,
  data: FilesFileSchemaPartial,
) {
  return httpPatchAssetsByAssetIdFilesByFileId(assetId, fileId, data);
}

export const deleteFilesetRequest = (
  assetId: string,
  filesetId: string,
  immediately: boolean,
) =>
  httpDeleteAssetsByAssetIdFileSetsByFileSetId(assetId, filesetId, {
    immediately: immediately ? 'true' : 'false',
  });

export const deleteAssetFileSet =
  (assetId: string, fileSetId: string, immediately = false) =>
  (dispatch: AppDispatch, getState: GetState): Promise<void> =>
    dispatch(deleteFilesetRequest(assetId, fileSetId, immediately)).then(() => {
      dispatch(successMessage({ text: 'Files will be deleted' }));
      const state = getState();
      const updatedEntities: PartialEntities = {};
      for (const file of selectFilesByFileSetId(state, fileSetId)) {
        if (!updatedEntities.files) {
          updatedEntities.files = {};
        }
        updatedEntities.files[file.id] = {
          status: 'DELETED',
        };
      }
      updatedEntities.file_sets = {
        [fileSetId]: {
          status: 'DELETED',
        },
      };
      dispatch(updateEntities(updatedEntities));
    });

export function createFileRequest(
  assetId: string,
  data: FilesFileCreateSchema,
) {
  return httpPostAssetsByAssetIdFiles(assetId, data);
}

export function createFileSetRequest(
  assetId: string,
  data: FilesFileSetSchema,
) {
  return httpPostAssetsByAssetIdFileSets(assetId, data);
}

export function createFormatRequest(assetId: string, data: FilesFormatSchema) {
  return httpPostAssetsByAssetIdFormats(assetId, data);
}

export function addFormat(data: FilesFormatSchemaWithId) {
  return (dispatch: AppDispatch) => {
    dispatch(updateEntities(normalize(data, entitySchemas.formats).entities));
  };
}

export function getOpenFile(elementId: string, fileId: string) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpGetAssetsByAssetIdFilesByFileIdIsgHandlerUrl(elementId, fileId),
    ).then((file) => {
      return file.body.url;
    });
}

export function openFile(elementId: string, fileId: string) {
  return (dispatch: AppDispatch): void => {
    dispatch(getOpenFile(elementId, fileId)).then((url) => {
      if (url) {
        window.location.href = url;
      }
    });
  };
}

export function getAssetFilesetsForFilesPanel(
  id: string,
  versionId?: string | undefined,
) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    dispatch(loadingAssetFormatsData(true));
    const userRoles = selectUserRoles(getState());
    const promises: Promise<unknown>[] = [];
    if (userRoles.canReadFiles) {
      promises.push(
        dispatch(getAssetFileSets(id, { file_count: true }, versionId)),
      );
    }
    return Promise.all(promises).finally(() => {
      dispatch(loadingAssetFormatsData(false));
    });
  };
}

export function setProxyResolutionIfNotSet(
  proxy: FilesProxySchemaWithId,
  width: number,
  height: number,
) {
  return (dispatch: AppDispatch): void => {
    if (!proxy.resolution) {
      dispatch(
        updateEntities({
          proxies: {
            [proxy.id]: {
              resolution: { width, height },
            },
          },
        }),
      );
    }
  };
}

export function openOriginalFile(
  assetId: string,
  formatId: string,
  fileId?: string,
) {
  return async (dispatch: AppDispatch): Promise<void> => {
    const error = "Can't open file";
    if (fileId) {
      dispatch(openFile(assetId, fileId));
    } else {
      const files = await dispatch(
        httpGetAssetsByAssetIdFiles(assetId, { per_page: 100 }),
      );
      const file = files.body.objects.find(
        (item) =>
          !item.url && item.status === 'CLOSED' && item.format_id === formatId,
      );
      const openId = file?.id;
      if (openId) {
        dispatch(openFile(assetId, openId));
      } else {
        dispatch(errorMessage({ text: error }));
      }
    }
  };
}

function downloadFormatFileUntracked(
  assetId: string,
  formatIdArgument?: string,
  fileSetIdArgument?: string,
  formatNameArgument?: string,
) {
  return async (dispatch: AppDispatch): Promise<DownloadHandledBy> => {
    const versionId = getCurrentRouteParams().versionId;

    const error = 'File not available to download';

    let formatId = formatIdArgument;
    let formatName = formatNameArgument;
    // if we don't have the format id we need to find it
    if (!formatId) {
      const formats = await dispatch(getAssetFormats(assetId, versionId));
      const originalCloudFormat = findOriginalCloudFormat(formats);
      formatId = originalCloudFormat?.id;
      formatName = formatName ?? originalCloudFormat?.name;
    }

    if (!formatId) {
      dispatch(errorMessage({ text: error }));
      return 'none';
    }

    let fileSetsPromise: Promise<FilesFileSetSchemaWithIdNullable[]>;
    let filesPromise: Promise<FilesFileSchemaWithIdNullable[]>;

    if (fileSetIdArgument) {
      fileSetsPromise = dispatch(
        httpGetAssetsByAssetIdFileSetsByFileSetId(assetId, fileSetIdArgument),
      ).then((response) => [response.body]);
      filesPromise = dispatch(
        httpGetAssetsByAssetIdFileSetsByFileSetIdFiles(
          assetId,
          fileSetIdArgument,
          {
            per_page: 100,
          },
        ),
      ).then((response) => response.body.objects);
    } else {
      fileSetsPromise = dispatch(getAssetFileSets(assetId, {}, versionId));
      filesPromise = dispatch(getAssetFiles(assetId, versionId));
    }

    const [fileSets, files] = await Promise.all([
      fileSetsPromise,
      filesPromise,
    ]);

    const activeFileSetIds = fileSets
      .filter(({ is_archive, status }) => status === 'ACTIVE' && !is_archive)
      .map(({ id }) => id);

    const activeCloudFiles = files.filter((file) => {
      // file should be in the desired file_set
      if (fileSetIdArgument && file.file_set_id !== fileSetIdArgument) {
        return false;
      }
      // Filter out files that are in deleted or archived file sets
      if (!activeFileSetIds.includes(file.file_set_id)) {
        return false;
      }
      // file should be in the desired format
      if (!file.storage_id || file.format_id !== formatId) {
        return false;
      }
      // file should be cloud and closed
      if (file.url && file.status === 'CLOSED') {
        return true;
      }
      return false;
    });

    if (activeCloudFiles.length > 0) {
      const filesByFileSet = groupBy(activeCloudFiles, 'file_set_id');
      const firstActiveFileSetId = Object.keys(filesByFileSet).at(0);
      if (firstActiveFileSetId) {
        const result = await dispatch(
          agentTryDownload({
            body: {
              fileSets: [{ fileSetId: firstActiveFileSetId, assetId }],
            },
            formats: formatName ? [formatName] : undefined,
          }),
        );
        if (result.type !== 'agent_not_running') {
          return 'agent';
        }
      }
      const fileSetValues = Object.values(filesByFileSet).filter(
        (item) => item,
      );
      if (fileSetValues.length < 1) {
        dispatch(errorMessage({ text: error }));
      }
      await dispatch(downloadAttempts(assetId, fileSetValues, 0, error));
      return 'browser';
    }
    dispatch(errorMessage({ text: error }));
    return 'none';
  };
}

export function downloadFormatFile(
  assetId: string,
  formatId?: string,
  fileSetId?: string,
  formatName?: string,
) {
  return async (dispatch: AppDispatch) => {
    const handledBy = await dispatch(
      downloadFormatFileUntracked(assetId, formatId, fileSetId, formatName),
    );
    if (handledBy === 'browser') {
      dispatch(
        trackBrowserAssetDownload(formatName ? [formatName] : undefined),
      );
    }
  };
}

function downloadProxyByIdUntracked(assetId: string, proxyId: string) {
  return (dispatch: AppDispatch): Promise<void> =>
    dispatch(
      httpGetAssetsByAssetIdProxiesByProxyIdDownloadUrl(assetId, proxyId),
    ).then((response) => {
      if (!response.body.url) {
        dispatch(errorMessage({ text: 'Unable to download file' }));
        return;
      }
      downloadUrlIframe(response.body.url);
    });
}

export function downloadProxyById(assetId: string, proxyId: string) {
  return async (dispatch: AppDispatch) => {
    await dispatch(downloadProxyByIdUntracked(assetId, proxyId));
    dispatch(trackBrowserAssetDownload([typeFormatMap.proxy]));
  };
}

export function downloadEditProxyFile(items: SearchItemEntity[]) {
  return async (dispatch: AppDispatch) => {
    const downloadableEditProxyItems = items.flatMap((item) => {
      const matchingFormats =
        item.formats?.filter(
          (f) => f.name === 'EDIT_PROXY' || f.name === 'PPRO_PROXY',
        ) ?? [];
      const matchingFormatIds = matchingFormats.map((f) => f.id);

      if (matchingFormatIds.length === 0) {
        return [];
      }

      return item.files
        ? item.files
            .filter(
              (file): file is typeof file & { format_id: string } =>
                typeof file.format_id === 'string' &&
                matchingFormatIds.includes(file.format_id),
            )
            .map((file) => ({
              formatId: file.format_id,
              formatName: matchingFormats.find(
                ({ id }) => id === file.format_id,
              )?.name,
              fileSetId: file.file_set_id,
              assetId: item.id,
            }))[0]
        : [];
    });

    const formats = uniqueFormats(
      downloadableEditProxyItems.map(({ formatName }) => formatName),
    );

    const itemsForAgentDownload = downloadableEditProxyItems.map(
      ({ assetId, fileSetId }) => ({
        assetId,
        fileSetId,
      }),
    );

    const result = await dispatch(
      agentTryDownload({
        body: { fileSets: itemsForAgentDownload },
        formats,
      }),
    );

    if (result.type !== 'agent_not_running') {
      return;
    }

    dispatch(
      trackBrowserAssetDownload(formats, downloadableEditProxyItems.length),
    );

    downloadableEditProxyItems.forEach(({ assetId, formatId, fileSetId }) => {
      dispatch(downloadFormatFileUntracked(assetId, formatId, fileSetId));
    });
  };
}

function downloadProxyFileUntracked(assetId: string, versionId: string) {
  return async (dispatch: AppDispatch): Promise<DownloadHandledBy> => {
    const result = await dispatch(
      agentTryDownload({
        body: {
          assetVersions: [{ assetId, versionId, format: typeFormatMap.proxy }],
        },
      }),
    );
    if (result.type !== 'agent_not_running') {
      return 'agent';
    }

    const response = await dispatch(
      httpGetAssetsByAssetIdVersionsByVersionIdProxies(assetId, versionId),
    );
    const error = 'Proxy file is not available to download';
    const cloudFile = response.body?.objects?.find(
      (file) => file.url && file.status === 'CLOSED',
    );
    if (!cloudFile) {
      dispatch(errorMessage({ text: error }));
      return 'none';
    }
    await dispatch(downloadProxyByIdUntracked(assetId, cloudFile.id));
    return 'browser';
  };
}

export function downloadProxyFile(assetId: string, versionId: string) {
  return async (dispatch: AppDispatch) => {
    const handledBy = await dispatch(
      downloadProxyFileUntracked(assetId, versionId),
    );
    if (handledBy === 'browser') {
      dispatch(trackBrowserAssetDownload([typeFormatMap.proxy]));
    }
  };
}

export function downloadSearchItems(
  items: SearchItemEntity[],
  type: 'proxy' | 'original' | 'all',
) {
  return async (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();

    if (
      type === 'original' ||
      (agentApiClient.hasFeature('FORMAT_DOWNLOAD') && type === 'proxy') ||
      (agentApiClient.hasFeature('DOWNLOAD_ALL_FORMATS') && type === 'all')
    ) {
      const result = await dispatch(
        agentTryDownload({
          body: itemsToDownloadRequest(items, {}, type),
        }),
      );
      if (result.type !== 'agent_not_running') {
        return;
      }
    }

    if (items.some((item) => item.object_type === OBJECT_TYPE_COLLECTIONS)) {
      dispatch(
        warningMessage({
          text: DownloadAgentErrorMessage({
            message:
              'The iconik Agent needs to be running to download collections.',
          }),
          timeout: 10000,
        }),
      );
    }
    if (type === 'all') {
      dispatch(
        warningMessage({
          text: DownloadAgentErrorMessage({
            message:
              'The iconik Agent needs to be running when trying to download all formats.',
          }),
          timeout: 10000,
        }),
      );
    }

    // Only an explicit version in the URL applies here: the items being
    // downloaded are not necessarily the asset the route points at, so each
    // one falls back to its own latest active version below.
    const routeParamsVersionId = getCurrentRouteParams().versionId;

    // Downloading all formats without the Agent is not supported
    let downloadedCount = 0;

    items
      .filter(
        (item) => item.object_type === OBJECT_TYPE_ASSETS && !item.is_blocked,
      )
      .forEach(({ id, title, files, formats }) => {
        if (!files || !files.length || !formats) {
          dispatch(errorMessage({ text: `${title} doesn't contain any file` }));
          return;
        }

        let originalFormat;
        switch (type) {
          case 'original':
            originalFormat = formats.find(
              (format) => format.name === 'ORIGINAL',
            );
            if (originalFormat) {
              dispatch(
                downloadFormatFileUntracked(
                  id,
                  originalFormat.id,
                  undefined,
                  originalFormat.name,
                ),
              );
              downloadedCount++;
            }
            break;
          case 'proxy':
            {
              const versionId =
                routeParamsVersionId ??
                selectLatestActiveVersionExtended(state, id)?.id;

              if (!versionId) {
                return;
              }

              dispatch(downloadProxyFileUntracked(id, versionId));
              downloadedCount++;
            }
            break;

          default:
            break;
        }
      });

    if (downloadedCount > 0) {
      dispatch(
        trackBrowserAssetDownload([typeFormatMap[type]], downloadedCount),
      );
    }
  };
}

export function deleteProxyById(proxyId: string) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const asset = getState().pages.asset.assetId;
    if (asset) {
      return dispatch(
        httpDeleteAssetsByAssetIdProxiesByProxyId(asset, proxyId),
      ).then(() => {
        dispatch(successMessage({ text: 'Proxy has been deleted' }));
        dispatch(deleteNormalizedEntity({ key: 'proxies', id: proxyId }));
      });
    }
    dispatch(errorMessage({ text: 'Asset not found' }));
    return Promise.reject();
  };
}

export function renameCurrentAssetFormat(
  assetId: string,
  formatId: string,
  name: string,
) {
  return (dispatch: AppDispatch): Promise<unknown> =>
    dispatch(
      httpPatchAssetsByAssetIdFormatsByFormatId(assetId, formatId, { name }),
    );
}
